import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import { GET_PAST_GROUP_EVENTS, GET_UPCOMING_GROUP_EVENTS } from './query'
import { GROUP_EVENT_PAGE_LIMIT } from 'lib/pga-dot-coach'
import useCreateEventQuickStart from './use-create-event-quick-start'
import { useFeatureFlags, flags } from 'lib/feature-flags'

const useGroupEventsQuery = (query, variables, skip) => {
  return useQuery(query, {
    variables,
    skip,
  })
}

const useGroupEventsPanel = ({ skip = false } = {}) => {
  const [selectedTab, setSelectedTab] = useState('upcoming')
  const [showCanceledEvents, setShowCanceledEvents] = useState(false)
  const [offset, setOffset] = useState(0)
  const [showLimitReachedDisclaimer, setShowLimitReachedDisclaimer] = useState(
    false,
  )
  const [openFreeTierLimitDialog, setOpenFreeTierLimitDialog] = useState(false)
  const quickStart = useCreateEventQuickStart({
    canBuildOwnEvent: !showLimitReachedDisclaimer,
  })
  const [isPgaHopeCoach, setIsPgaHopeCoach] = useState(false)
  const [withRecurrence] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_RECURRING_INSTANCES,
  ])

  useEffect(() => {
    setOffset(0)
  }, [selectedTab, showCanceledEvents])

  const showPastEvents = selectedTab === 'past'
  const startDateTime = showPastEvents
    ? {
      from: null,
      to: DateTime.local()
        .startOf('day')
        .toISO(),
    }
    : {
      from: DateTime.now()
        .startOf('day')
        .toISO(),
      to: null,
    }

  const pastEventsVariables = {
    orderBy: { field: 'START_DATE_TIME', direction: 'DESC' },
    startDateTime,
    includeCanceled: showCanceledEvents,
    limit: GROUP_EVENT_PAGE_LIMIT,
    offset,
    withRecurrence: !!withRecurrence,
    skip: selectedTab !== 'past',
  }

  const upcomingEventsVariables = {
    orderBy: { field: 'START_DATE_TIME', direction: 'ASC' },
    startDateTime,
    includeCanceled: showCanceledEvents,
    limit: GROUP_EVENT_PAGE_LIMIT,
    offset,
    withRecurrence: !!withRecurrence,
    skip: selectedTab === 'past',
  }

  const pastEventsResult = useGroupEventsQuery(
    GET_PAST_GROUP_EVENTS,
    pastEventsVariables,
    skip || selectedTab !== 'past',
  )
  const upcomingEventsResult = useGroupEventsQuery(
    GET_UPCOMING_GROUP_EVENTS,
    upcomingEventsVariables,
    skip || selectedTab === 'past',
  )

  const queryResult =
    selectedTab === 'past' ? pastEventsResult : upcomingEventsResult
  const { data, loading, error, refetch } = queryResult

  const handleCreateEventClick = () => {
    if (showLimitReachedDisclaimer) {
      setOpenFreeTierLimitDialog(true)
      return
    }
    quickStart.open()
  }

  return {
    selectedTab,
    setSelectedTab,
    showCanceledEvents,
    setShowCanceledEvents,
    offset,
    setOffset,
    showLimitReachedDisclaimer,
    setShowLimitReachedDisclaimer,
    openFreeTierLimitDialog,
    setOpenFreeTierLimitDialog,
    showCreateEventQuickStart: quickStart.isOpen,
    handleCreateEventQuickStartCancel: quickStart.overlayProps.onCancel,
    handleCreateEventQuickStartContinue: quickStart.overlayProps.onContinue,
    handleCreateEventQuickStartSourceSelected:
      quickStart.overlayProps.onSourceSelected,
    isPgaHopeCoach,
    setIsPgaHopeCoach,
    data,
    loading,
    error,
    refetch,
    handleCreateEventClick,
  }
}

export default useGroupEventsPanel
